import React, { useEffect, useMemo } from 'react';
import AcaoRealizadaComp from '@components/Solicitacao/Shared/Historico/AcaoRealizada/Index.jsx';
import { formatDateTimeToBR } from '@utils/FormatDate.jsx';
import { useFetchSolicitacaoHistoricoByNumber } from '@hooks/api/Solicitacao/useFetchSolicitacaoHistoricoByNumber.js';
import { ConfigProvider } from 'antd';
import { StyledTable } from '@styles/datatables/datatable-antd.js';
import ptBR from 'antd/es/locale/pt_BR';
import { useSelector } from 'react-redux';
import { selectHistoricoBySolicitacaoId } from '@store/selectors/solicitacaoHistoricoSelector.js';
import PerfilHistoricoComp from '../../Historico/PerfilHistorico/Index';

const GridHistoricoSolicitacao = ({ idSolicitacao, setData }) => {
  const {
    fetchSolicitacaoHistorico,
    solicitacaoHistoricoOptions,
    solicitacaoHistoricoLoading,
    totalItens,
    currentPage,
    rowsPerPage,
  } = useFetchSolicitacaoHistoricoByNumber();

  const historicoRedux = useSelector(selectHistoricoBySolicitacaoId(idSolicitacao));

  const historicoCombinado = useMemo(() => {
    const idsAPI = new Set(solicitacaoHistoricoOptions.map(h => h.id));
    const apenasNovos = historicoRedux.filter(h => !idsAPI.has(h.id));
    return [...apenasNovos, ...solicitacaoHistoricoOptions];
  }, [solicitacaoHistoricoOptions, historicoRedux]);

  // Fetch sempre que a página muda
  useEffect(() => {
    if (idSolicitacao) {
      fetchSolicitacaoHistorico(idSolicitacao, currentPage, rowsPerPage);
    }
  }, [idSolicitacao, currentPage, rowsPerPage]);

  // Atualiza dados no state externo
  useEffect(() => {
    setData(solicitacaoHistoricoOptions, totalItens);
  }, [solicitacaoHistoricoOptions, totalItens]);

  const columns = useMemo(() => [
    {
      title: 'DATA',
      dataIndex: 'data',
      key: 'data',
      sorter: (a, b) => a.data.localeCompare(b.data),
      render: (text) => <div style={{ whiteSpace: 'normal' }}>{formatDateTimeToBR(text)}</div>,
    },
    {
      title: 'NOME DO COLABORADOR',
      dataIndex: 'nomeColaborador',
      key: 'nomeColaborador',
      width: '35%',
      sorter: (a, b) => a.nomeColaborador.localeCompare(b.nomeColaborador),
      render: (text) => <div style={{ whiteSpace: 'normal' }}>{text}</div>,
    },
    {
      title: 'PERFIL',
      dataIndex: 'perfilColaborador',
      key: 'perfil',
      width: '15%',
      sorter: (a, b) => a.perfilColaborador.localeCompare(b.perfilColaborador),
      render: (text, record) => (
        <PerfilHistoricoComp perfil={record.perfilColaborador} />
      )
    },
    {
      title: 'AÇÃO REALIZADA',
      key: 'acaoRealizada',
      sorter: (a, b) => a.acaoRealizada - b.acaoRealizada,
      render: (text, record) => (
        <AcaoRealizadaComp acaoRealizada={record.acaoRealizada} />
      ),
    },
  ], []);

  return (
    <div className="px-3 mt-4">
      <ConfigProvider locale={ptBR}>
        <StyledTable
          rowKey="id"
          columns={columns}
          dataSource={historicoCombinado}
          loading={solicitacaoHistoricoLoading}
          pagination={{
            defaultPageSize: 5,
            showSizeChanger: true,
            pageSizeOptions: ['5', '10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`
          }}
          scroll={{ y: 300, x: 'max-content' }}
          locale={{
            emptyText: <h3 className='m-3'>Nenhum dado disponível para exibição.</h3>
          }}
        />
      </ConfigProvider>
    </div>
  );
};

export default GridHistoricoSolicitacao;
